import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";

const API_BASE = "/api/repricing";

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const statusColor = (status) => {
  if (status === "ready") return "success";
  if (status === "attention") return "warning";
  if (status === "blocked") return "error";
  return "default";
};

const metricLabel = (key) =>
  ({
    stale_ebay_data: "Stale eBay data",
    failed_sync: "Failed sync",
    latest_snapshot_at: "Latest snapshot",
    latest_traffic_date: "Latest traffic",
    available_modes: "Campaign modes",
    generated_count: "Latest run",
    approved_count: "Approved",
    pending_review_count: "Pending review",
    blocked_count: "Blocked",
    row_count: "Push queue",
    eligible_count: "Eligible",
    rejected_count: "Blocked",
    stale_ebay_data_count: "Stale push data",
    recent_failed_pushes: "Recent failed pushes",
  }[key] || key.replaceAll("_", " "));

const metricValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") return numberText(value);
  return String(value);
};

export default function RepricingWeeklyRunbook({ onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/weekly-runbook`);
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || json.message || "Failed to load weekly runbook");
      }
      setData(json);
    } catch (e) {
      setData(null);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openModule = (module, params = {}) => {
    const detail = { module, params };
    window.dispatchEvent(new CustomEvent("app:navigate-module", { detail }));
  };

  const steps = data?.steps || [];
  const summary = data?.summary || {};

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Weekly Operator Runbook
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Follow this order each week: refresh intelligence, pick a campaign, create/review a run, then only use the guarded push queue after approval.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadData} disabled={loading}>
          Refresh Status
        </Button>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {data?.store_signal_warnings?.map((warning) => (
        <Alert key={warning} severity="warning">{warning}</Alert>
      ))}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Alert severity={summary.stale_ebay_data ? "warning" : "success"}>
            Stale eBay data: {numberText(summary.stale_ebay_data)}
          </Alert>
        </Grid>
        <Grid item xs={12} md={3}>
          <Alert severity={summary.approved_push_queue_rows ? "warning" : "info"}>
            Approved push queue: {numberText(summary.approved_push_queue_rows)}
          </Alert>
        </Grid>
        <Grid item xs={12} md={3}>
          <Alert severity={summary.recent_failed_pushes ? "warning" : "success"}>
            Failed pushes/errors: {numberText(summary.recent_failed_pushes)}
          </Alert>
        </Grid>
        <Grid item xs={12} md={3}>
          <Alert severity={summary.latest_verified_run_id ? "success" : "info"}>
            Latest run: {summary.latest_verified_run_id || "none"}
          </Alert>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {steps.map((step, index) => (
          <Grid item xs={12} md={6} xl={4} key={step.key}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {index + 1}. {step.label}
                    </Typography>
                    <Chip size="small" color={statusColor(step.status)} label={step.status || "planned"} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {step.helper}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {Object.entries(step.metrics || {}).map(([key, value]) => (
                      <Chip key={key} size="small" variant="outlined" label={`${metricLabel(key)}: ${metricValue(value)}`} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {step.target_tab && (
                      <Button size="small" variant="contained" onClick={() => onOpenTab?.(step.target_tab)}>
                        Open Step
                      </Button>
                    )}
                    {step.target_module && (
                      <Button size="small" variant="outlined" onClick={() => openModule(step.target_module)}>
                        Open Module
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
